import { SmsPacingSettings } from './WorkflowBuilder.types';

export const SMS_PACING_MIN = 1;
export const SMS_PACING_MAX = 60;

/** Backend default when the workflow stores no smsPacing. */
export const SMS_PACING_DEFAULT = 6;

/**
 * Hydrates the input value from the stored config. Returns '' when nothing is
 * stored, so an unconfigured workflow renders blank and the next save leaves
 * the key out instead of writing a value nobody chose (SLAI-2729).
 */
export const parseSmsPacingFormValue = (sp?: SmsPacingSettings): string =>
    typeof sp?.maxPerMinute === 'number' ? String(sp.maxPerMinute) : '';

/** A blank input is unset, which is always allowed; a filled one is range checked. */
export const validateSmsPacingMaxPerMinute = (value: string): string | true => {
    if (value.trim() === '') return true;
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < SMS_PACING_MIN || parsed > SMS_PACING_MAX) {
        return `Enter a whole number between ${SMS_PACING_MIN} and ${SMS_PACING_MAX}`;
    }
    return true;
};

/**
 * Decides what to send for the smsPacing key on save.
 *
 * - undefined: omit the key entirely, leaving the stored value untouched. The
 *   only shape that lets an unconfigured workflow stay unconfigured after an
 *   unrelated edit (the same regression SLAI-2729 fixed for queueCadence).
 * - null: clear the stored value, so the workflow falls back to the default.
 * - { maxPerMinute: N }: set the stored value.
 *
 * `loaded` is what the form was hydrated with. Comparing against it keeps the
 * key out of the payload when the user has not touched it, and distinguishes
 * "cleared a previously set value" (send null) from "left a never-set field
 * blank" (omit).
 */
export const buildSmsPacingUpdate = (current: string, loaded: string): SmsPacingSettings | null | undefined => {
    if (current === loaded) return undefined;
    if (current.trim() === '') return null;
    const parsed = Number(current);
    if (!Number.isInteger(parsed)) return undefined;
    return { maxPerMinute: parsed };
};
